/**
 * Agency Redux Slice
 * Manages agency profile state for agency_admin users
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { axiosInstance } from '../../api/axios.config';
import type { AgencyProfileResponse } from '../../types/agency.types';

/**
 * Agency State Interface
 */
interface AgencyState {
  agency: AgencyProfileResponse | null;
  isLoading: boolean;
  error: string | null;
  lastFetched: string | null;
}

/**
 * Initial State
 */
const initialState: AgencyState = {
  agency: null,
  isLoading: false,
  error: null,
  lastFetched: null,
};

/**
 * Fetch Agency by ID
 * GET /api/v1/agencies/{id}
 */
export const fetchAgencyByIdAsync = createAsyncThunk<
  AgencyProfileResponse,
  string,
  { rejectValue: string }
>(
  'agency/fetchById',
  async (agencyId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get<{ success: boolean; data: AgencyProfileResponse }>(
        `/api/v1/agencies/${agencyId}`
      );

      if (!response.data.success) {
        throw new Error('Failed to fetch agency data');
      }

      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch agency data.'
      );
    }
  }
);

/**
 * Fetch My Agency Profile (merged with draft revision when applicable)
 * GET /api/v1/agencies/{id}
 */
export const fetchMyAgencyProfileAsync = createAsyncThunk<
  AgencyProfileResponse,
  string,
  { rejectValue: string }
>(
  'agency/fetchMyProfile',
  async (agencyId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get<{ success: boolean; data: AgencyProfileResponse }>(
        `/api/v1/agencies/${agencyId}`
      );

      if (!response.data.success) {
        throw new Error('Failed to fetch agency profile')
      }

      return response.data.data
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch agency profile.'
      )
    }
  }
)

/**
 * Agency Slice
 */
const agencySlice = createSlice({
  name: 'agency',
  initialState,
  reducers: {
    // Set agency manually
    setAgency: (state, action: PayloadAction<AgencyProfileResponse>) => {
      state.agency = action.payload;
      state.lastFetched = new Date().toISOString();
    },

    // Clear agency data
    clearAgency: (state) => {
      state.agency = null;
      state.error = null;
      state.lastFetched = null;
    },

    // Update agency data partially
    updateAgency: (state, action: PayloadAction<Partial<AgencyProfileResponse>>) => {
      if (state.agency) {
        state.agency = { ...state.agency, ...action.payload };
      }
    },

    // Clear error
    clearAgencyError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Agency by ID
    builder
      .addCase(fetchAgencyByIdAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAgencyByIdAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.agency = action.payload;
        state.error = null;
        state.lastFetched = new Date().toISOString();
      })
      .addCase(fetchAgencyByIdAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch agency data';
      });

    // Fetch My Profile
    builder
      .addCase(fetchMyAgencyProfileAsync.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchMyAgencyProfileAsync.fulfilled, (state, action) => {
        state.isLoading = false
        state.agency = action.payload
        state.error = null
        state.lastFetched = new Date().toISOString()
      })
      .addCase(fetchMyAgencyProfileAsync.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || 'Failed to fetch agency profile'
      })
  },
});

// Actions
export const { setAgency, clearAgency, updateAgency, clearAgencyError } = agencySlice.actions;

// Selectors
export const selectAgency = (state: { agency: AgencyState }) => state.agency.agency;
export const selectAgencyLoading = (state: { agency: AgencyState }) => state.agency.isLoading;
export const selectAgencyError = (state: { agency: AgencyState }) => state.agency.error;
export const selectAgencyLastFetched = (state: { agency: AgencyState }) => state.agency.lastFetched;

// Derived selectors
export const selectAgencyInfo = (state: { agency: AgencyState }) => state.agency.agency?.agencyInfo;
export const selectAgencyAddresses = (state: { agency: AgencyState }) => state.agency.agency?.addresses;
export const selectAgencyContact = (state: { agency: AgencyState }) => state.agency.agency?.contact;
export const selectAgencyLicensing = (state: { agency: AgencyState }) => state.agency.agency?.licensing;
export const selectAgencyServices = (state: { agency: AgencyState }) => state.agency.agency?.services;
export const selectAgencyOperations = (state: { agency: AgencyState }) => state.agency.agency?.operations;
export const selectAgencyInsurance = (state: { agency: AgencyState }) => state.agency.agency?.insurance;
export const selectAgencyDocuments = (state: { agency: AgencyState }) => state.agency.agency?.documents;
export const selectAgencyStatus = (state: { agency: AgencyState }) => state.agency.agency?.status;

// Reducer
export default agencySlice.reducer;
